import { useEffect, useState } from 'react';
import {
  FaListUl,
  FaServer,
  FaChevronLeft,
  FaChevronRight,
  FaCirclePlay,
} from 'react-icons/fa6';
import { FaSync } from 'react-icons/fa';

import PlaylistSearch from './PlaylistSearch';
import ProvidersTab from '../Providers/ProvidersTab';
import { ShimmerPlaylistCard } from '../Shimmers/ShimmerPlaylistCard';

const token = localStorage.getItem('prae.jwt');

/** Construit la query string vers l’API avec tous les filtres/tri/pagination */
function buildQuery({ page, pageSize, search, category, provider, sortBy }) {
  const qs = new URLSearchParams();
  qs.set('page', String(page));
  qs.set('pageSize', String(pageSize));
  if (search) qs.set('search', search);
  if (category) qs.set('category', String(category));
  if (provider) qs.set('provider', String(provider));
  if (sortBy) qs.set('sortBy', sortBy);
  return qs.toString();
}

function Playlist() {
  const [playlists, setPlaylists] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [pageCount, setPageCount] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('playlist');
  const [refreshTick, setRefreshTick] = useState(0);

  // État de la recherche/tri (persisté en localStorage)
  const [searchProps, setSearchProps] = useState(() => {
    try {
      const fromLS = localStorage.getItem('playlistSearchProps');
      return fromLS
        ? JSON.parse(fromLS)
        : { search: '', category: '', provider: '', sortBy: 'progress' };
    } catch {
      return { search: '', category: '', provider: '', sortBy: 'progress' };
    }
  });

  // Persistance locale
  useEffect(() => {
    localStorage.setItem('playlistSearchProps', JSON.stringify(searchProps));
  }, [searchProps]);

  // Reset de page quand la recherche/les filtres/tri changent (valeurs "commitées")
  useEffect(() => {
    setPage(1);
  }, [
    searchProps.search,
    searchProps.category,
    searchProps.provider,
    searchProps.sortBy,
  ]);

  // Handlers d’actions (doivent être dans le composant pour accéder à setPage)
  const handleSoftDelete = async playlistId => {
    try {
      const wasLastItemOnPage = playlists.length <= 1;

      const resp = await fetch(
        `${process.env.REACT_APP_API_URL}/playlists/${playlistId}`,
        {
          method: 'DELETE',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );
      if (!resp.ok && resp.status !== 204) {
        throw new Error(`HTTP ${resp.status}`);
      }

      if (wasLastItemOnPage && page > 1) {
        setPage(p => Math.max(1, p - 1));
      } else {
        setRefreshTick(t => t + 1);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleRestore = async playlistId => {
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_API_URL}/playlists/${playlistId}/restore`,
        {
          method: 'POST',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );
      if (!resp.ok && resp.status !== 204) {
        throw new Error(`HTTP ${resp.status}`);
      }
      setRefreshTick(t => t + 1);
    } catch (e) {
      console.error(e);
    }
  };

  // Fetch paginé + filtré côté back (avec annulation)
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const fetchPlaylists = async () => {
      setLoading(true);
      try {
        const qs = buildQuery({
          page,
          pageSize,
          search: searchProps.search, // déjà débouncé + seuilé dans PlaylistSearch
          category: searchProps.category,
          provider: searchProps.provider,
          sortBy: searchProps.sortBy,
        });

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/playlists/stats/grouped?${qs}`,
          {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              'Content-Type': 'application/json',
            },
            signal: controller.signal,
          }
        );
        if (!response.ok) throw new Error(`Erreur HTTP : ${response.status}`);
        const data = await response.json();

        if (!cancelled) {
          setPlaylists(Array.isArray(data.playlists) ? data.playlists : []);
          setTotal(Number(data.total) || 0);
          setPageCount(Number(data.pageCount) || 1);
          setError(null);
        }
      } catch (err) {
        if (err?.name === 'AbortError') return;
        if (!cancelled) setError(err.message || 'Erreur inconnue');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchPlaylists();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    page,
    pageSize,
    token,
    searchProps.search,
    searchProps.category,
    searchProps.provider,
    searchProps.sortBy,
    refreshTick,
  ]);

  // UI helpers
  const TabButton = ({ id, icon: Icon, label }) => (
    <button
      onClick={() => setTab(id)}
      aria-pressed={tab === id}
      className={`relative inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl text-sm font-medium transition-all border
        ${
          tab === id
            ? 'bg-white border-slate-200 shadow-sm text-slate-900'
            : 'bg-slate-100 hover:bg-slate-50 border-transparent text-slate-500'
        }
      `}
    >
      <Icon className="opacity-80" /> {label}
      {tab === id && (
        <span className="absolute inset-x-3 -bottom-0.5 h-0.5 rounded-full bg-indigo-500" />
      )}
    </button>
  );

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-slate-50 to-white">
      {/* <header className="sticky top-0 z-30 backdrop-blur bg-white/70 border-b border-slate-200">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-600 text-white shadow-sm">
              <FaCirclePlay />
            </span>
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-400">
                Bibliothèque
              </div>
              <div className="font-semibold text-slate-900">
                Vos playlists & providers
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <TabButton
              id="playlist"
              icon={FaListUl}
              label={`Playlists (${total || 0})`}
            />
            <TabButton id="provider" icon={FaServer} label="Providers SMTP" />
          </div>
        </div>
      </header> */}

      <main className="w-full px-4 sm:px-6 lg:px-8 py-6">
        {tab === 'playlist' && (
          <div className="space-y-6">
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4 sm:p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="font-semibold text-slate-900">
                  Rechercher / filtrer
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setLoading(true);
                    setPage(p => p); // force un refetch
                  }}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm"
                >
                  <FaSync className="opacity-80" /> Actualiser
                </button>
              </div>

              <PlaylistSearch
                playlistsData={loading ? [] : playlists}
                loading={loading}
                searchProps={searchProps}
                setSearchProps={setSearchProps}
                onSoftDelete={handleSoftDelete}
                onRestore={handleRestore}
              />
            </div>

            {loading ? (
              <ShimmerPlaylistCard count={pageSize} />
            ) : error ? (
              <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 p-4">
                Une erreur est survenue : {error}
              </div>
            ) : (
              <div className="w-full" />
            )}

            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
              <div className="text-sm text-slate-500">
                Page {page} / {pageCount} — {total} élément
                {total > 1 ? 's' : ''}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(p - 1, 1))}
                  disabled={page <= 1 || loading}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 text-slate-700 disabled:opacity-50 hover:bg-slate-50"
                >
                  <FaChevronLeft /> Précédent
                </button>
                <button
                  onClick={() => setPage(p => Math.min(p + 1, pageCount))}
                  disabled={page >= pageCount || loading}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 text-slate-700 disabled:opacity-50 hover:bg-slate-50"
                >
                  Suivant <FaChevronRight />
                </button>
              </div>
            </div>
          </div>
        )}

        {tab === 'provider' && (
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4 sm:p-5">
            <ProvidersTab />
          </div>
        )}
      </main>
    </div>
  );
}

export default Playlist;
